import { supabase } from '../lib/supabase';

// ============================================================
// TRAINING CATALOG SERVICE (master library)
// ============================================================
export const trainingCatalogService = {
  async getAll(filters = {}) {
    let query = supabase
      ?.from('training_catalog')
      ?.select('*')
      ?.order('created_at', { ascending: false });
    if (filters?.status && filters?.status !== 'all') {
      query = query?.eq('catalog_status', filters?.status);
    }
    if (filters?.tier && filters?.tier !== 'all') {
      query = query?.eq('target_tier', filters?.tier);
    }
    if (filters?.category && filters?.category !== 'all') {
      query = query?.eq('category', filters?.category);
    }
    if (filters?.search) {
      query = query?.ilike('title', `%${filters?.search}%`);
    }
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async create(catalogData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase
      ?.from('training_catalog')
      ?.insert({ ...catalogData, created_by: user?.id })
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async update(id, updates) {
    const { data, error } = await supabase
      ?.from('training_catalog')
      ?.update({ ...updates, updated_at: new Date()?.toISOString() })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async remove(id) {
    const { error } = await supabase?.from('training_catalog')?.delete()?.eq('id', id);
    if (error) throw error;
  }
};

// ============================================================
// PARTNER TRAINING ASSIGNMENTS SERVICE
// ============================================================
export const partnerTrainingAssignmentsService = {
  async getByPartner(partnerId) {
    const { data, error } = await supabase
      ?.from('partner_training_assignments')
      ?.select('*, training_catalog(*)')
      ?.eq('partner_id', partnerId)
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async getAll(filters = {}) {
    let query = supabase
      ?.from('partner_training_assignments')
      ?.select('*, training_catalog(*), partners(legal_name, brand_name, tier)')
      ?.order('created_at', { ascending: false });
    if (filters?.partnerId) query = query?.eq('partner_id', filters?.partnerId);
    if (filters?.status && filters?.status !== 'all') query = query?.eq('assignment_status', filters?.status);
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async assign(assignmentData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase
      ?.from('partner_training_assignments')
      ?.insert({ ...assignmentData, assigned_by: user?.id })
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async update(id, updates) {
    const { data, error } = await supabase
      ?.from('partner_training_assignments')
      ?.update({ ...updates, updated_at: new Date()?.toISOString() })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async remove(id) {
    const { error } = await supabase?.from('partner_training_assignments')?.delete()?.eq('id', id);
    if (error) throw error;
  }
};

// ============================================================
// PARTNER CERTIFICATIONS SERVICE (with document upload)
// ============================================================
export const partnerCertificationsService = {
  async getAll(partnerId) {
    let query = supabase
      ?.from('partner_certifications')
      ?.select('*, partners(legal_name, brand_name)')
      ?.order('expiry_date', { ascending: true });
    if (partnerId) query = query?.eq('partner_id', partnerId);
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async getExpiring(daysAhead = 30) {
    const futureDate = new Date();
    futureDate?.setDate(futureDate?.getDate() + daysAhead);
    const { data, error } = await supabase
      ?.from('partner_certifications')
      ?.select('*, partners(legal_name, brand_name)')
      ?.lte('expiry_date', futureDate?.toISOString()?.split('T')?.[0])
      ?.gte('expiry_date', new Date()?.toISOString()?.split('T')?.[0])
      ?.order('expiry_date', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  async create(certData) {
    const { data, error } = await supabase
      ?.from('partner_certifications')
      ?.insert(certData)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async update(id, updates) {
    const { data, error } = await supabase
      ?.from('partner_certifications')
      ?.update({ ...updates, updated_at: new Date()?.toISOString() })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async uploadDocument(certId, partnerId, file) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const filePath = `${partnerId}/certifications/${certId}_${Date.now()}_${file?.name}`;
    const { error: uploadError } = await supabase?.storage
      ?.from('partner-documents')
      ?.upload(filePath, file, { upsert: true });
    if (uploadError) throw uploadError;
    const { data, error } = await supabase
      ?.from('partner_certifications')
      ?.update({ document_path: filePath, document_name: file?.name })
      ?.eq('id', certId)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async getDocumentUrl(filePath) {
    const { data, error } = await supabase?.storage
      ?.from('partner-documents')
      ?.createSignedUrl(filePath, 3600);
    if (error) throw error;
    return data?.signedUrl;
  },

  async remove(id) {
    const { error } = await supabase?.from('partner_certifications')?.delete()?.eq('id', id);
    if (error) throw error;
  }
};

// ============================================================
// CAPABILITY ASSESSMENTS SERVICE
// ============================================================
export const capabilityAssessmentsService = {
  async getByPartner(partnerId) {
    const { data, error } = await supabase
      ?.from('capability_assessments')
      ?.select('*')
      ?.eq('partner_id', partnerId)
      ?.order('assessment_date', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async getLatestByPartner(partnerId) {
    const { data, error } = await supabase
      ?.from('capability_assessments')
      ?.select('*')
      ?.eq('partner_id', partnerId)
      ?.order('assessment_date', { ascending: false })
      ?.limit(1)
      ?.single();
    if (error && error?.code !== 'PGRST116') throw error;
    return data || null;
  },

  async create(assessmentData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase
      ?.from('capability_assessments')
      ?.insert({ ...assessmentData, assessed_by: user?.id })
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async update(id, updates) {
    const { data, error } = await supabase
      ?.from('capability_assessments')
      ?.update({ ...updates, updated_at: new Date()?.toISOString() })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  }
};

// ============================================================
// LEARNING PATHS SERVICE
// ============================================================
export const learningPathsService = {
  async getAll(filters = {}) {
    let query = supabase
      ?.from('learning_paths')
      ?.select('*, learning_path_items(*, training_catalog(*))')
      ?.order('created_at', { ascending: false });
    if (filters?.tier && filters?.tier !== 'all') query = query?.eq('target_tier', filters?.tier);
    if (filters?.status && filters?.status !== 'all') query = query?.eq('path_status', filters?.status);
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async create(pathData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase
      ?.from('learning_paths')
      ?.insert({ ...pathData, created_by: user?.id })
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async update(id, updates) {
    const { data, error } = await supabase
      ?.from('learning_paths')
      ?.update({ ...updates, updated_at: new Date()?.toISOString() })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async addItem(pathId, trainingCatalogId, sortOrder = 0, isRequired = true) {
    const { data, error } = await supabase
      ?.from('learning_path_items')
      ?.insert({ learning_path_id: pathId, training_catalog_id: trainingCatalogId, sort_order: sortOrder, is_required: isRequired })
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async removeItem(itemId) {
    const { error } = await supabase?.from('learning_path_items')?.delete()?.eq('id', itemId);
    if (error) throw error;
  },

  async getPartnerAssignments(partnerId) {
    const { data, error } = await supabase
      ?.from('partner_learning_path_assignments')
      ?.select('*, learning_paths(*, learning_path_items(*, training_catalog(*)))')
      ?.eq('partner_id', partnerId);
    if (error) throw error;
    return data || [];
  },

  async assignToPartner(partnerId, learningPathId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase
      ?.from('partner_learning_path_assignments')
      ?.insert({ partner_id: partnerId, learning_path_id: learningPathId, assigned_by: user?.id, assignment_status: 'in_progress', started_at: new Date()?.toISOString() })
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  }
};

// ============================================================
// ENABLEMENT REPORTS SERVICE
// ============================================================
export const enablementReportsService = {
  async getTrainingReport(filters = {}) {
    let query = supabase
      ?.from('partner_trainings')
      ?.select('*, partners(legal_name, brand_name, tier)')
      ?.order('created_at', { ascending: false });
    if (filters?.partnerId) query = query?.eq('partner_id', filters?.partnerId);
    if (filters?.status && filters?.status !== 'all') query = query?.eq('training_status', filters?.status);
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async getCertificationsReport(filters = {}) {
    let query = supabase
      ?.from('partner_certifications')
      ?.select('*, partners(legal_name, brand_name, tier)')
      ?.order('expiry_date', { ascending: true });
    if (filters?.partnerId) query = query?.eq('partner_id', filters?.partnerId);
    if (filters?.status && filters?.status !== 'all') query = query?.eq('cert_status', filters?.status);
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async getAssignmentsReport(filters = {}) {
    let query = supabase
      ?.from('partner_training_assignments')
      ?.select('*, training_catalog(title, category, difficulty_level), partners(legal_name, brand_name, tier)')
      ?.order('created_at', { ascending: false });
    if (filters?.partnerId) query = query?.eq('partner_id', filters?.partnerId);
    if (filters?.status && filters?.status !== 'all') query = query?.eq('assignment_status', filters?.status);
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async getCapabilityReport(filters = {}) {
    let query = supabase
      ?.from('capability_assessments')
      ?.select('*, partners(legal_name, brand_name, tier)')
      ?.order('assessment_date', { ascending: false });
    if (filters?.partnerId) query = query?.eq('partner_id', filters?.partnerId);
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }
};
